/*
 * kcc-grind.js — "Grind it for me"
 *
 * Writes the shopper's brew-method choice into two line item properties on the
 * product form so it reaches the cart and the order:
 *
 *   properties[Grind]                  "Espresso — Fine"
 *   properties[_Grind acknowledgement] "Accepted <copy> on <ISO date>"
 *
 * The inputs are `disabled` until a grind is confirmed. Disabled fields are
 * omitted from FormData, so an untouched product posts no grind property at all
 * rather than an empty one — an empty property would still show up on the order.
 */
(function () {
  'use strict';

  var ACK_PREFIX = 'Accepted: ';

  function init(root) {
    if (root.__kccGrindReady) return;
    root.__kccGrindReady = true;

    var modal    = root.querySelector('[data-kcc-grind-modal]');
    var openBtn  = root.querySelector('[data-kcc-grind-open]');
    var clearBtn = root.querySelector('[data-kcc-grind-clear]');
    var valueEl  = root.querySelector('[data-kcc-grind-value]');
    var ackEl    = root.querySelector('[data-kcc-grind-ack]');
    var labelEl  = root.querySelector('[data-kcc-grind-label]');
    var subEl    = root.querySelector('[data-kcc-grind-sub]');
    var actionEl = root.querySelector('[data-kcc-grind-action]');
    if (!modal || !openBtn || !valueEl) return;

    /* A `position: fixed` element is only viewport-relative when no ancestor creates a
       containing block — any transform/filter/will-change up the tree (and this modal
       lives deep inside the product form) re-anchors it and clips the top off-screen.
       Reparenting to <body> makes the overlay reliable. The hidden inputs stay behind in
       the form; this closure already holds direct references to them. */
    if (modal.parentNode !== document.body) document.body.appendChild(modal);

    var methods   = Array.prototype.slice.call(modal.querySelectorAll('[data-kcc-grind-method]'));
    var ackInput  = modal.querySelector('[data-kcc-grind-ack-input]');
    var confirmEl = modal.querySelector('[data-kcc-grind-confirm]');
    var ackCopy   = ackInput ? (ackInput.closest('label') || {}).textContent || '' : '';

    var defaults = {
      label:  labelEl ? labelEl.textContent.trim() : 'Grind it for me',
      sub:    subEl ? subEl.textContent.trim() : '',
      action: actionEl ? actionEl.textContent.trim() : 'Choose'
    };

    var pending = null;   // {label, setting} chosen inside the modal
    var applied = null;   // what is actually on the form
    var lastFocus = null;

    /* ---------- selection state inside the modal ---------- */

    function paint(btn) {
      methods.forEach(function (m) {
        var on = m === btn;
        m.setAttribute('aria-checked', on ? 'true' : 'false');
        m.classList.toggle('border-kcc-forest', on);
        m.classList.toggle('bg-kcc-off-white', on);
        m.classList.toggle('ring-1', on);
        m.classList.toggle('ring-kcc-forest', on);
        m.classList.toggle('border-kcc-border', !on);
      });
    }

    function refreshConfirm() {
      if (!confirmEl) return;
      confirmEl.disabled = !(pending && ackInput && ackInput.checked);
    }

    methods.forEach(function (btn) {
      btn.addEventListener('click', function () {
        pending = { label: btn.getAttribute('data-label'), setting: btn.getAttribute('data-setting') };
        paint(btn);
        refreshConfirm();
      });
    });

    if (ackInput) ackInput.addEventListener('change', refreshConfirm);

    /* ---------- applying to the form ---------- */

    function apply(choice) {
      applied = choice;
      if (choice) {
        valueEl.value = choice.label + ' — ' + choice.setting;
        valueEl.disabled = false;
        if (ackEl) {
          ackEl.value = ACK_PREFIX + ackCopy.replace(/\s+/g, ' ').trim();
          ackEl.disabled = false;
        }
        if (labelEl)  labelEl.textContent  = 'Ground for ' + choice.label;
        if (subEl)    subEl.textContent    = choice.setting;
        if (actionEl) actionEl.textContent = 'Change';
        if (clearBtn) clearBtn.classList.remove('hidden');
        openBtn.classList.add('border-kcc-forest', 'bg-kcc-off-white');
      } else {
        valueEl.value = '';
        valueEl.disabled = true;
        if (ackEl) { ackEl.value = ''; ackEl.disabled = true; }
        if (labelEl)  labelEl.textContent  = defaults.label;
        if (subEl)    subEl.textContent    = defaults.sub;
        if (actionEl) actionEl.textContent = defaults.action;
        if (clearBtn) clearBtn.classList.add('hidden');
        openBtn.classList.remove('border-kcc-forest', 'bg-kcc-off-white');
        pending = null;
        paint(null);
        if (ackInput) ackInput.checked = false;
        refreshConfirm();
      }
    }

    /* ---------- open / close ---------- */

    function focusables() {
      return Array.prototype.slice.call(
        modal.querySelectorAll('button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])')
      ).filter(function (el) { return el.offsetParent !== null; });
    }

    function open() {
      lastFocus = document.activeElement;
      modal.classList.remove('hidden');
      modal.classList.add('flex');
      document.body.style.overflow = 'hidden';
      // reopening starts from whatever is currently applied
      if (applied) {
        var match = methods.filter(function (m) { return m.getAttribute('data-label') === applied.label; })[0];
        if (match) { pending = applied; paint(match); }
        if (ackInput) ackInput.checked = true;
      }
      refreshConfirm();
      var f = focusables();
      if (f.length) f[0].focus();
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      modal.classList.add('hidden');
      modal.classList.remove('flex');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeydown);
      // discard an unconfirmed selection
      if (!applied) { pending = null; paint(null); if (ackInput) ackInput.checked = false; }
      refreshConfirm();
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    function onKeydown(e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab') return;
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    openBtn.addEventListener('click', open);
    if (clearBtn) clearBtn.addEventListener('click', function () { apply(null); });

    modal.querySelectorAll('[data-kcc-grind-close], [data-kcc-grind-cancel], [data-kcc-grind-overlay]')
      .forEach(function (el) { el.addEventListener('click', close); });

    if (confirmEl) {
      confirmEl.addEventListener('click', function () {
        if (!pending || (ackInput && !ackInput.checked)) return;
        apply(pending);
        close();
      });
    }

    /* A variant change swaps the form's inputs but not this block — the choice
       intentionally persists across sizes. Nothing to do. */
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-kcc-grind-root]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
